import React, { useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import { Grid } from "@mui/material";

// custom imports
import PageHeader from "../../components/PageHeader";
import FormDialog from "../../forms/FormDialog";
import CustomTable from "../../components/Tables/CustomTable";
import FormInput from "../../forms/FormInput";
import CustomSelect from "../../components/SecondaryForms/CustomSelect";
import WarehouseContext from "../../context/WarehouseContext";
import CustomSearch from "../../components/Tables/CustomSearch";

// services
import { del, get, post, put } from "../../services/apiMethods";
import URLS from "../../services/urlConstants";

// data
import { visitorPurposeTableKeys } from "../../data/TableData/visitorPurpose";

export default function VisitorPurpose() {
  const [isReady, setIsReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [dataToEdit, setDataToEdit] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const { allWarehouses } = useContext(WarehouseContext);
  const [readOnly, setReadOnly] = useState(false);
  const [searchFilter, setSearchFilter] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setWarehouses(
      allWarehouses?.map((s) => ({
        ...s,
        label: s.code,
        value: s._id,
      })) || []
    );
    setSelectedWarehouse(allWarehouses[0]?._id || "");
  }, [allWarehouses]);

  const getData = async () => {
    setIsReady(false);
    try {
      const { data } = await get(URLS.visitorPurpose.list, {
        params: { warehouse: selectedWarehouse },
      });
      setData(data.result);
    } catch (error) {
      console.error(error);
    }
    setTimeout(() => {
      setIsReady(true);
    }, 300);
  };

  useEffect(() => {
    if (selectedWarehouse) {
      getData();
    } else {
      setIsReady(true);
    }
  }, [selectedWarehouse]);

  const handleCreateOrUpdate = async (values) => {
    try {
      setLoading(true);
      if (dataToEdit) {
        await put(
          URLS.visitorPurpose.update + "/" + dataToEdit._id,
          values
        );
      } else {
        await post(URLS.visitorPurpose.create, values);
      }
      getData();
      handleModalClose();
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const formik = useFormik({
    initialValues: {
      warehouse: selectedWarehouse,
      name: dataToEdit?.name || "",
      description: dataToEdit?.description || "",
    },
    onSubmit: handleCreateOrUpdate,
    enableReinitialize: true,
  });

  const openDataAddOrUpdateModal = () => setOpen(true);

  const handleModalClose = () => {
    setOpen(false);
    setTimeout(() => {
      setDataToEdit(null);
      formik.resetForm();
    }, 200);
  };

  const handleSetDataToEdit = (data) => {
    setReadOnly(false);
    setDataToEdit(data);
    setOpen(true);
  };

  const handleShowData = (data) => {
    setReadOnly(true);
    setDataToEdit(data);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await del(URLS.visitorPurpose.delete + "/" + id);
      setData(data.filter((item) => item._id !== id));
    } catch (error) {
      console.error(error);
    }
  };

  const handleChangeWarehouse = (e) => {
    setSelectedWarehouse(e.target.value);
    formik.setFieldValue("warehouse", e.target.value);
  };

  useEffect(() => {
    if (selectedWarehouse) {
      setFilterData(
        data.filter((item) => item.warehouse?._id === selectedWarehouse)
      );
    } else {
      setFilterData(data);
    }
  }, [data, selectedWarehouse]);

  return (
    <>
      <PageHeader
        title="Visitor Purpose"
        onAddClick={openDataAddOrUpdateModal}
        module="Visitor Purpose"
        addButtonDisabled={!selectedWarehouse}
      />

      <Grid
        container
        spacing={2}
        my={2}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Grid item xs={12} md={6} lg={3}>
          <CustomSelect
            required={true}
            name="warehouse"
            onChange={handleChangeWarehouse}
            label="Select Warehouse"
            options={warehouses}
            value={selectedWarehouse}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3} marginTop={2}>
          <CustomSearch
            data={filterData}
            setSearch={setSearch}
            setSearchFilter={setSearchFilter}
            search={search}
            placeholder="Search Purpose"
            keys={["name"]}
          />
        </Grid>
      </Grid>

      <CustomTable
        actions={["edit", "delete", "view"]}
        bodyDataModal="visitorPurpose"
        isDataReady={isReady}
        bodyData={search ? searchFilter : filterData}
        tableKeys={visitorPurposeTableKeys}
        onDeleteClick={handleDelete}
        onEditClick={handleSetDataToEdit}
        onViewClick={handleShowData}
        module="Visitor Purpose"
      />

      <FormDialog
        readOnly={readOnly}
        open={open}
        onClose={handleModalClose}
        submitButtonTitle={dataToEdit ? "Update" : "Add"}
        formik={formik}
        formTitle={
          dataToEdit ? "Update Visitor Purpose" : "Add Visitor Purpose"
        }
        adding={loading}
      >
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={12} sm={6}>
            <FormInput
              required={true}
              name="name"
              type="text"
              formik={formik}
              label="Name"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput
              name="description"
              type="text"
              formik={formik}
              label="Description"
            />
          </Grid>
        </Grid>
      </FormDialog>
    </>
  );
}
